import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Reveal, SunMark } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTEREST_OPTIONS = [
  { value: "camps", label: "Camps" },
  { value: "clinics", label: "Clinics" },
  { value: "both", label: "Both" },
  { value: "getaway", label: "The Getaway (Adults)" },
];

const emptyForm = {
  parent_name: "",
  athlete_name: "",
  athlete_age: "",
  email: "",
  interested_in: "",
  phone: "",
  experience: "",
  club: "",
};

export const InterestList = () => {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const val = e.detail;
      if (["camps", "clinics", "both", "getaway"].includes(val)) {
        setForm((f) => ({ ...f, interested_in: val }));
      }
      setDone(false);
    };
    window.addEventListener("bb:interest", handler);
    return () => window.removeEventListener("bb:interest", handler);
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.parent_name.trim()) return setError("Please add the parent/guardian name.");
    if (!form.athlete_name.trim()) return setError("Please add the athlete name.");
    if (!form.athlete_age.trim()) return setError("Please add the athlete age.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return setError("Please add a valid email.");
    if (!form.interested_in) return setError("Please choose Camps, Clinics, or Both.");

    try {
      setSending(true);
      const res = await axios.post(`${API}/interest-list`, form);
      if (res.data?.ok) {
        setDone(true);
      } else {
        setError(res.data?.error || "Something went wrong. Please try again.");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="join" className="relative bg-deep text-cream py-20 md:py-28 overflow-hidden">
      <SunMark className="absolute -top-8 right-8 w-72 h-48 text-cream/[0.06] rotate-6 pointer-events-none" />
      <p className="absolute bottom-2 left-0 w-full font-display uppercase text-[clamp(3rem,12vw,9rem)] leading-none text-outline-cream text-center pointer-events-none select-none whitespace-nowrap">
        Summer 2027
      </p>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-sun flex items-center gap-2">
              <span className="w-8 h-px bg-sun" /> Tampa, Florida
            </p>
            <h2 className="mt-4 font-display uppercase leading-[0.95] text-[clamp(2.6rem,6vw,4.5rem)]">
              Join the interest list.
            </h2>
            <p className="mt-6 text-base md:text-lg text-cream/80 leading-relaxed max-w-md">
              Be first to hear about Summer 2027 camps, clinics, and registration.
            </p>
            <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.22em] text-cream/60">
              Good Vibes × Volleyball
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.1}>
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                data-testid="interest-success"
                className="bg-cream text-deep rounded-2xl p-10 text-center"
              >
                <motion.div
                  initial={{ scale: 0, rotate: -30 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.15 }}
                  className="inline-block text-sun"
                >
                  <SunMark className="w-16 h-12" />
                </motion.div>
                <h3 className="mt-5 font-display uppercase text-3xl md:text-4xl">You're on the list!</h3>
                <p className="mt-4 text-sm md:text-base text-deep-soft leading-relaxed max-w-sm mx-auto">
                  Thanks, {form.parent_name.trim().split(" ")[0]}! Keep an eye on your inbox — Summer
                  2027 Beach Break news is coming your way.
                </p>
                <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-deep/60">
                  Good Vibes × Volleyball
                </p>
                <button
                  data-testid="interest-reset-button"
                  onClick={() => {
                    setForm(emptyForm);
                    setDone(false);
                  }}
                  className="mt-8 font-mono text-[11px] uppercase tracking-[0.18em] underline underline-offset-4 text-deep hover:text-sun transition-colors"
                >
                  Add another athlete
                </button>
              </motion.div>
            ) : (
              <form
                data-testid="interest-form"
                onSubmit={submit}
                className="bg-cream text-deep rounded-2xl p-6 md:p-8 shadow-2xl"
                noValidate
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] block mb-1.5" htmlFor="bb-parent">
                      Parent/Guardian Name *
                    </label>
                    <input
                      id="bb-parent"
                      data-testid="interest-input-parent"
                      className="bb-field"
                      placeholder="Jordan Rivers"
                      value={form.parent_name}
                      onChange={set("parent_name")}
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] block mb-1.5" htmlFor="bb-athlete">
                      Athlete Name *
                    </label>
                    <input
                      id="bb-athlete"
                      data-testid="interest-input-athlete"
                      className="bb-field"
                      placeholder="Sam Rivers"
                      value={form.athlete_name}
                      onChange={set("athlete_name")}
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] block mb-1.5" htmlFor="bb-age">
                      Athlete Age *
                    </label>
                    <input
                      id="bb-age"
                      data-testid="interest-input-age"
                      className="bb-field"
                      placeholder="15"
                      inputMode="numeric"
                      value={form.athlete_age}
                      onChange={set("athlete_age")}
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] block mb-1.5" htmlFor="bb-email">
                      Email *
                    </label>
                    <input
                      id="bb-email"
                      data-testid="interest-input-email"
                      className="bb-field"
                      type="email"
                      placeholder="you@email.com"
                      value={form.email}
                      onChange={set("email")}
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] mb-2">Interested In *</p>
                  <div className="flex flex-wrap gap-2">
                    {INTEREST_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        data-testid={`interest-chip-${opt.value}`}
                        onClick={() => setForm((f) => ({ ...f, interested_in: opt.value }))}
                        className={`font-mono text-[11px] uppercase tracking-[0.16em] px-5 py-2.5 rounded-full border transition-colors duration-200 ${
                          form.interested_in === opt.value
                            ? "bg-deep text-cream border-deep"
                            : "border-deep/25 text-deep hover:border-deep"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-5 grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] block mb-1.5" htmlFor="bb-phone">
                      Phone
                    </label>
                    <input
                      id="bb-phone"
                      data-testid="interest-input-phone"
                      className="bb-field"
                      placeholder="Optional"
                      value={form.phone}
                      onChange={set("phone")}
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] block mb-1.5" htmlFor="bb-exp">
                      Volleyball Experience
                    </label>
                    <input
                      id="bb-exp"
                      data-testid="interest-input-experience"
                      className="bb-field"
                      placeholder="Optional"
                      value={form.experience}
                      onChange={set("experience")}
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] block mb-1.5" htmlFor="bb-club">
                      Current Club/Team
                    </label>
                    <input
                      id="bb-club"
                      data-testid="interest-input-club"
                      className="bb-field"
                      placeholder="Optional"
                      value={form.club}
                      onChange={set("club")}
                    />
                  </div>
                </div>

                {error && (
                  <p
                    role="alert"
                    data-testid="interest-form-error"
                    className="mt-4 text-sm font-semibold text-[#B4552A] bg-sun/15 border border-sun/40 rounded-lg px-4 py-2.5"
                  >
                    {error}
                  </p>
                )}

                <button
                  data-testid="interest-submit-button"
                  type="submit"
                  disabled={sending}
                  className="mt-6 w-full bg-deep text-cream font-mono text-xs uppercase tracking-[0.18em] px-6 py-4 rounded-full hover:bg-sun hover:text-deep transition-colors duration-200 disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Join the Interest List"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default InterestList;
